import type { ICellRendererParams } from 'ag-grid-community'
import { BBDTag, MaterialIcon } from '@gravitate-js/excalibrr'
import { Popover } from 'antd'
import type { MatchStatus } from '../../../../../../api/useSupplierReconciliation/types/common'
import { MATCH_STATUS_CONFIG } from '../../../constants/statusConfig'
import { OnHoldInfoPopover } from '../../OnHoldInfoPopover'
import type { MatchDetailResponse } from '@/api/useSupplierReconciliation/useMatchDetail'

// Helper function to map display values back to raw status values
function getRawStatusFromDisplayValue(displayValue: string): MatchStatus | null {
  // Check if it's already a raw status value
  if (displayValue in MATCH_STATUS_CONFIG) {
    return displayValue as MatchStatus
  }

  // Map display values back to raw status values
  const displayToRawMap: Record<string, MatchStatus> = {
    Unapproved: 'unapproved',
    Approved: 'approved',
    'On Hold': 'hold',
    Unmatched: 'unmatched',
    'Unmatched Invoice': 'unmatched',
    'Unmatched BOL': 'unmatched',
    Unmapped: 'unmapped',
    Voided: 'void',
    Unvoided: 'unvoided',
  }

  return displayToRawMap[displayValue] || null
}

export function StatusCellRenderer({ value, data }: ICellRendererParams<MatchDetailResponse, MatchStatus>) {
  if (!value) return null

  // Get the raw status value for config lookup
  const rawStatus = getRawStatusFromDisplayValue(value)
  if (!rawStatus) return value

  const config = MATCH_STATUS_CONFIG[rawStatus]
  if (!config) return value

  // For unmatched status, determine the specific unmatched type and customize icon
  const getIconElement = () => {
    if (rawStatus === 'unmatched' && data) {
      const hasInvoiceIds = data.invoice_numbers && data.invoice_numbers.some((inv: any) => inv.id)
      const hasBolIds = data.bol_numbers && data.bol_numbers.some((bol: any) => bol.id)

      if (hasInvoiceIds && !hasBolIds) {
        return <span style={{ fontSize: '8px', fontWeight: 'bold', marginRight: '4px' }}>INV</span>
      } else if (!hasInvoiceIds && hasBolIds) {
        return <span style={{ fontSize: '8px', fontWeight: 'bold', marginRight: '4px' }}>BOL</span>
      }
    }
    // Default to material icon for all other cases
    return <MaterialIcon icon={config.icon} fill size={8} className='mr-2' />
  }

  const statusTag = (
    <BBDTag
      style={{ width: 'min-content' }}
      error={config.tagProps.error}
      warning={config.tagProps.warning}
      success={config.tagProps.success}
      theme2={config.tagProps.theme2}
      icon={getIconElement()}
    >
      {config.display}
    </BBDTag>
  )

  if (rawStatus === 'hold' && data) {
    return (
      <Popover content={<OnHoldInfoPopover data={data} />} title={null} trigger='hover' placement='top'>
        <div style={{ cursor: 'pointer' }}>{statusTag}</div>
      </Popover>
    )
  }

  return statusTag
}
